import React, { useRef, useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAudio } from '../../context/AudioContext';

const BAR_COUNT = 72;
// Deterministic waveform silhouette (no Math.random — SSR-safe)
const WAVEFORM = Array.from({ length: BAR_COUNT }, (_, i) => {
  const a = Math.sin(i * 0.42) * 0.5 + 0.5;
  const b = Math.sin(i * 1.17 + 1.3) * 0.3 + 0.5;
  const c = ((i * 7919) % 23) / 23;
  return Math.max(0.18, Math.min(1, a * 0.55 + b * 0.35 + c * 0.2));
});

const fmt = (s) => {
  if (!isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

export default function Player() {
  const { tracks, current, playing, setCurrent, setPlaying } = useAudio();
  const track = tracks[current];
  const audioRef = useRef(null);
  const seekRef = useRef(null);

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hoverPct, setHoverPct] = useState(null); // 0..1 or null
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(0.85);
  const [liveBars, setLiveBars] = useState(null); // Float32 of length BAR_COUNT or null

  // Web Audio analyser — lazily created on first play
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const rafRef = useRef(0);
  const smoothRef = useRef(new Float32Array(BAR_COUNT));

  const ensureAnalyser = useCallback(() => {
    const a = audioRef.current;
    if (!a || sourceRef.current) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const src = ctx.createMediaElementSource(a);
      const an = ctx.createAnalyser();
      an.fftSize = 256; // 128 frequency bins
      an.smoothingTimeConstant = 0.78;
      src.connect(an);
      an.connect(ctx.destination);
      audioCtxRef.current = ctx;
      sourceRef.current = src;
      analyserRef.current = an;
    } catch (_) { /* ignore — fallback to static waveform */ }
  }, []);

  useEffect(() => {
    const tick = () => {
      const an = analyserRef.current;
      if (an) {
        const bins = an.frequencyBinCount; // 128
        const data = new Uint8Array(bins);
        an.getByteFrequencyData(data);
        const out = smoothRef.current;
        // Map 128 bins → BAR_COUNT, log-ish weighting so lows don't drown highs
        const usable = Math.floor(bins * 0.85);
        for (let i = 0; i < BAR_COUNT; i++) {
          const t = i / (BAR_COUNT - 1);
          // bias toward lower-mid frequencies for visual punch
          const binIdx = Math.floor(Math.pow(t, 1.4) * usable);
          const v = data[binIdx] / 255;
          // smooth: rise fast, fall slow
          out[i] = v > out[i] ? out[i] * 0.55 + v * 0.45 : out[i] * 0.86 + v * 0.14;
        }
        setLiveBars(Float32Array.from(out));
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // Play / pause + track changes
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      ensureAnalyser();
      if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume();
      }
      a.play().catch(() => setPlaying(false));
    } else {
      a.pause();
    }
  }, [playing, current, setPlaying, ensureAnalyser]);

  useEffect(() => {
    const a = audioRef.current;
    if (a) { a.load(); setCurrentTime(0); }
  }, [current]);

  useEffect(() => {
    const a = audioRef.current;
    if (a) { a.volume = volume; a.muted = muted; }
  }, [volume, muted]);

  const onTimeUpdate = useCallback(() => {
    const a = audioRef.current;
    if (a) setCurrentTime(a.currentTime);
  }, []);

  const onLoadedMeta = useCallback(() => {
    const a = audioRef.current;
    if (a) setDuration(a.duration || 0);
  }, []);

  const seekToClient = useCallback((clientX) => {
    const el = seekRef.current;
    const a = audioRef.current;
    if (!el || !a || !duration) return;
    const r = el.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    a.currentTime = pct * duration;
    setCurrentTime(pct * duration);
  }, [duration]);

  const onSeekClick = (e) => seekToClient(e.clientX);
  const onSeekMove = (e) => {
    const r = seekRef.current?.getBoundingClientRect();
    if (!r) return;
    setHoverPct(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)));
  };

  const progressPct = duration > 0 ? currentTime / duration : 0;
  const headPct = hoverPct ?? progressPct;

  const onKeyDown = (e) => {
    const a = audioRef.current;
    if (!a) return;
    if (e.key === 'ArrowRight') { a.currentTime = Math.min(duration, a.currentTime + 2); }
    else if (e.key === 'ArrowLeft') { a.currentTime = Math.max(0, a.currentTime - 2); }
    else if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setPlaying(p => !p); }
  };

  // Detect compact viewport — hide waveform under 720px
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 720px)');
    const u = () => setCompact(mq.matches);
    u();
    mq.addEventListener('change', u);
    return () => mq.removeEventListener('change', u);
  }, []);

  const gridCols = compact
    ? '44px 1fr auto auto'
    : '56px minmax(160px, 220px) auto auto 1fr auto auto';

  return (
    <div
      role="region"
      aria-label="Audio player"
      style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 60,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        height: compact ? 64 : 80,
        padding: compact ? '0 14px' : '0 22px',
        background: 'linear-gradient(180deg, rgba(8,8,16,0.96), rgba(4,4,10,0.99))',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        display: 'grid',
        gridTemplateColumns: gridCols,
        alignItems: 'center',
        gap: compact ? 12 : 18,
      }}
    >
      <audio
        ref={audioRef}
        src={track.file || undefined}
        preload="metadata"
        onTimeUpdate={onTimeUpdate}
        onLoadedMetadata={onLoadedMeta}
        onDurationChange={onLoadedMeta}
        onEnded={() => setCurrent(c => (c + 1) % tracks.length)}
      />

      {/* Artwork */}
      <div style={{
        width: compact ? 44 : 56, height: compact ? 44 : 56,
        overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)',
        flexShrink: 0, position: 'relative',
      }}>
        <img src="/pochette front.JPG" alt="A Piece of Sky"
             style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>

      {/* Track info */}
      <div style={{ overflow: 'hidden', minWidth: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          fontFamily: 'var(--mono)', fontSize: compact ? 12 : 13, fontWeight: 500,
          color: '#fff', letterSpacing: '0.02em',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {track.title}
          <span style={{
            flexShrink: 0,
            fontSize: 9, letterSpacing: '0.18em',
            color: 'rgba(255,255,255,0.55)',
            border: '1px solid rgba(255,255,255,0.18)',
            padding: '2px 6px',
          }}>PREVIEW</span>
        </div>
        <div style={{
          fontFamily: 'var(--mono)', fontSize: compact ? 10 : 11,
          color: 'var(--faint)', marginTop: 3, letterSpacing: '0.06em',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          A Piece of Sky · Tsanga
        </div>
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 6 : 10 }}>
        <motion.button
          onClick={() => setCurrent(c => (c - 1 + tracks.length) % tracks.length)}
          whileTap={{ scale: 0.9 }}
          aria-label="Previous track"
          style={iconBtn(compact)}
        >
          <SkipIcon dir="prev" />
        </motion.button>

        <motion.button
          onClick={() => setPlaying(p => !p)}
          onKeyDown={onKeyDown}
          whileTap={{ scale: 0.93 }}
          aria-label={playing ? 'Pause' : 'Play'}
          style={{
            width: compact ? 32 : 38, height: compact ? 32 : 38,
            borderRadius: '50%', background: '#fff', border: 0,
            display: 'grid', placeItems: 'center', cursor: 'pointer',
            flexShrink: 0, color: '#000',
            transition: 'transform 0.2s',
          }}
        >
          {playing ? <PauseIcon /> : <PlayIcon />}
        </motion.button>

        <motion.button
          onClick={() => setCurrent(c => (c + 1) % tracks.length)}
          whileTap={{ scale: 0.9 }}
          aria-label="Next track"
          style={iconBtn(compact)}
        >
          <SkipIcon dir="next" />
        </motion.button>
      </div>

      {/* Elapsed time */}
      {!compact && (
        <span style={timeStyle}>{fmt(currentTime)}</span>
      )}

      {/* Waveform seekbar */}
      {!compact && (
        <div
          ref={seekRef}
          role="slider"
          tabIndex={0}
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={Math.max(1, Math.round(duration))}
          aria-valuenow={Math.round(currentTime)}
          onClick={onSeekClick}
          onMouseMove={onSeekMove}
          onMouseLeave={() => setHoverPct(null)}
          onKeyDown={onKeyDown}
          style={{
            position: 'relative',
            display: 'flex', alignItems: 'center', gap: 2,
            height: 36, cursor: 'pointer', overflow: 'visible',
            userSelect: 'none',
          }}
        >
          {WAVEFORM.map((staticAmp, i) => {
            const pct = i / (BAR_COUNT - 1);
            const passed = pct <= progressPct;
            const inHover = hoverPct !== null && pct > progressPct && pct <= hoverPct;
            const live = liveBars ? liveBars[i] : 0;
            // Blend: when playing, live FFT drives height with the static silhouette as floor
            const amp = playing
              ? Math.max(staticAmp * 0.35, Math.min(1, live * 1.15 + staticAmp * 0.2))
              : staticAmp;
            return (
              <div key={i} style={{
                flex: 1, minWidth: 1,
                height: `${Math.round(amp * 100)}%`,
                background: passed
                  ? 'rgba(255,255,255,0.92)'
                  : inHover
                    ? 'rgba(255,255,255,0.45)'
                    : 'rgba(255,255,255,0.16)',
                transition: playing ? 'background 0.15s' : 'height 0.25s, background 0.15s',
              }} />
            );
          })}
          {/* Hover playhead */}
          {hoverPct !== null && (
            <div style={{
              position: 'absolute', top: 0, bottom: 0,
              left: `${headPct * 100}%`,
              width: 1, background: 'rgba(255,255,255,0.5)',
              pointerEvents: 'none',
            }} />
          )}
        </div>
      )}

      {/* Total time + volume */}
      {!compact && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={timeStyle}>{fmt(duration)}</span>
          <VolumeControl
            muted={muted}
            volume={volume}
            onToggleMute={() => setMuted(m => !m)}
            onChange={(v) => { setVolume(v); if (v > 0) setMuted(false); }}
          />
        </div>
      )}
    </div>
  );
}

const iconBtn = (compact) => ({
  background: 'transparent', border: 0,
  color: 'rgba(255,255,255,0.65)',
  width: compact ? 26 : 30, height: compact ? 26 : 30,
  display: 'grid', placeItems: 'center', cursor: 'pointer',
  transition: 'color 0.15s',
});

const timeStyle = {
  fontFamily: 'var(--mono)', fontSize: 10,
  letterSpacing: '0.12em',
  color: 'rgba(255,255,255,0.55)',
  fontVariantNumeric: 'tabular-nums',
  minWidth: 36, textAlign: 'center',
  flexShrink: 0,
};

function PlayIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
      <path d="M2 1.5 L10 6 L2 10.5 Z" />
    </svg>
  );
}
function PauseIcon() {
  return (
    <svg width="10" height="11" viewBox="0 0 10 11" fill="currentColor" aria-hidden="true">
      <rect x="0" y="0" width="3" height="11" />
      <rect x="7" y="0" width="3" height="11" />
    </svg>
  );
}
function SkipIcon({ dir }) {
  const next = dir === 'next';
  return (
    <svg width="14" height="11" viewBox="0 0 14 11" fill="currentColor" aria-hidden="true"
         style={{ transform: next ? 'none' : 'scaleX(-1)' }}>
      <path d="M0 0.5 L8 5.5 L0 10.5 Z" />
      <rect x="10" y="0.5" width="2" height="10" />
    </svg>
  );
}

function VolumeControl({ muted, volume, onToggleMute, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8 }}
    >
      <button
        onClick={onToggleMute}
        aria-label={muted ? 'Unmute' : 'Mute'}
        style={iconBtn(false)}
      >
        <VolumeIcon level={muted ? 0 : volume} />
      </button>
      <motion.div
        initial={false}
        animate={{ width: open ? 72 : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
        style={{ overflow: 'hidden', display: 'flex', alignItems: 'center' }}
      >
        <input
          type="range"
          min={0} max={1} step={0.01}
          value={muted ? 0 : volume}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          aria-label="Volume"
          style={{
            width: 72, accentColor: '#fff',
            background: 'transparent',
          }}
        />
      </motion.div>
    </div>
  );
}

function VolumeIcon({ level }) {
  const wave = level > 0.05;
  const wave2 = level > 0.5;
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor"
         strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 5 L1 9 L3.5 9 L7 12 L7 2 L3.5 5 Z" fill="currentColor" />
      {wave && <path d="M9.5 5.2 Q11 7 9.5 8.8" />}
      {wave2 && <path d="M11 3.5 Q13.5 7 11 10.5" />}
    </svg>
  );
}
